import {EventEmitter, Inject, Injectable, Output} from '@angular/core';
import {BaseService} from "@shared/services/base.service";
import {ErrorHandlingService} from "@shared/services/error-handling.service";
import {HttpClient} from "@angular/common/http";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {combineLatest, forkJoin, from, iif, Observable, of, ReplaySubject, Subject} from "rxjs";
import {LoadingComponent} from "../modals/loading/loading.component";
import {Router} from "@angular/router";
import {StandbyApplicationComponent} from "../modals/standby-application/standby-application.component";
import {catchError, filter, finalize, map, shareReplay, switchMap, tap} from "rxjs/operators";
import {ResumeComponent} from '../modals/resume/resume.component';
import {LoginComponent} from '../modals/login/login.component';
import {StartComponent} from '../modals/start/start.component';

export interface Page {
  name: string;
  item: string;
  route?: string;
  service: BaseService;
  canActivate?: boolean;
  order: number;
  display_fields?: any;
  additional_types?: string[];
  // stateParams?: any; todo: remove?
  post_validation?: () => Observable<any>;
  required?: boolean;
  onItemChange?: (item: any, page: Page) => void;
  getPrintUrl?: (id: string) => {};
  /** Custom DRF action name to call on submit (defaults to 'submit'). */
  submitAction?: string;
  // Optional per-page overrides for where data/meta are read/written.
  getAttributes?: (attributes: any, meta: any, page: Page) => any;
  getMeta?: (attributes: any, meta: any, page: Page) => any;
  setAttributes?: (attributes: any, value: any, page: Page) => void;
}

export interface ApplicationType {
  component: any;
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  applicationType: string;
  attributes: any = {};
  meta: any = {};
  pages: ReplaySubject<Page[]> = new ReplaySubject<Page[]>();
  activePage: ReplaySubject<string> = new ReplaySubject<string>();
  title: ReplaySubject<string> = new ReplaySubject<string>();
  isNew: boolean;
  standbyService: BaseService
  standbyAnnualService: BaseService
  pesticidesService: BaseService
  burialAtSeaService: BaseService

  applicationTypes: {
    [key: string]: {
      component: any,
      service: BaseService,
      extraFields?: string[],
      id_field_label: string
    }
  };
  facilityPage: Page;
  contactsPage: Page;


  constructor(private http: HttpClient, private modalService: NgbModal, private errorHandling: ErrorHandlingService,
              private router: Router, @Inject('environment') private environment
  ) {
    this.standbyService = new BaseService(`${environment.baseUrl}/applications/standby`, this.http);
    this.standbyAnnualService = new BaseService(`${environment.baseUrl}/applications/standby/annualreports`, this.http);
    this.pesticidesService = new BaseService(`${environment.baseUrl}/applications/pesticides/noa`, this.http);
    this.burialAtSeaService = new BaseService(`${environment.baseUrl}/applications/burialatsea`, this.http);

    this.applicationTypes = {
      standby: {
        component: StandbyApplicationComponent,
        service: this.standbyService,
        id_field_label: 'Application #'
      },
      standbyannualreport: {
        component: LoginComponent,
        service: this.standbyAnnualService,
        extraFields: ['reporting_year'],
        id_field_label: 'Permit #'
      },
      pesticides_noa: {
        component: StartComponent,
        service: this.pesticidesService,
        id_field_label: 'Site ID'
      },
      burial_at_sea: {
        component: StartComponent,
        service: this.burialAtSeaService,
        id_field_label: 'Application # or Control #'
      }
    }

    this.facilityPage = {
      name: 'Facility Info',
      item: 'site',
      route: 'application/standby/site',
      canActivate: false,
      service: new BaseService(`${this.environment.baseUrl}/applications/facilities`, this.http),
      order: 2,
      display_fields: [
        'name', 'streetaddress', 'village', 'location_description', 'classification', 'attachment',
        'shape'
      ]
    };
    this.contactsPage = {
      name: 'Owner/Contact Info',
      item: 'contacts',
      route: 'application/standby/contacts',
      canActivate: true,
      service: new BaseService(`${this.environment.baseUrl}/applications/standby/contacts`, this.http),
      order: 1,
      display_fields: {
        individual: [
          'relationship_type', 'contact_contact_type', 'contact_first_name', 'contact_last_name', 'contact_title',
          'contact_division_name', 'contact_phone_number', 'contact_alternate_phone',
          'contact_email_address'
        ],
        organization: [
          'relationship_type', 'contact_contact_type', 'contact_org_name',
          'contact_phone_number', 'contact_alternate_phone', 'contact_email_address'
        ]
      },
      onItemChange: (item: any, page?: Page) => {
        if (!item.contact_contact_type) {
          // if contact type not set default to individual
          item.contact_contact_type = 'individual'
        }
        item._meta.display_fields = page.display_fields[item.contact_contact_type];

        if (!item.permits) {
          item.permits = this.attributes.globalid;
        }
        // item._meta.fields.relationship_type.get_choices = (search: string) => {
        //   return [];
        // }
        // item._meta.fields.relationship_type.choices = [...this.meta.fields.contacts.child.fields.relationship_type.choices.filter(choice => choice.value === item.relationship_type)];

        console.log(item._meta.fields.relationship_type);
        item._meta.fields.relationship_type.read_only = item.relationship_type !== 'additional' && item.relationship_type !== undefined;
        if (!item.relationship_type) {
          item._meta.required = false;
          item.relationship_type = 'additional';
          item._meta.fields.relationship_type.choices = this.meta.fields.contacts.child.fields.relationship_type.choices.filter(choice => choice.value === 'additional');
        }
      },
      additional_types: ['additional']
    }

  }

  loadPages(type) {
    const _pages: Page[] = []
    this.applicationType = type;
    if (type === 'standby') {
      _pages.push(
        {
          name: 'Generators',
          item: 'generators',
          route: 'application/standby/generators',
          canActivate: false,
          service: new BaseService(`${this.environment.baseUrl}/applications/standby/generators`, this.http),
          order: 3,
          display_fields: {
            common: [
              'generator_make', 'generator_model', 'generator_serial_number', 'generator_plate',
              'date_of_manufacture', 'service_start_date', 'unit_identification', 'description',
              'fuel_type', 'fuel_consumption',
              'brake_horsepower', 'brake_kilowatts',
              'shape',
              'engine_plate', 'engine_make', 'engine_model', 'engine_serial_number',
              'engine_hours', 'picture',
              'tank_location', 'tank_type',
              'useAP42',
              'certificate',
              'document'
            ],
            total_unburned_hydrocarbons: ['total_unburned_hydrocarbons', 'toc_units'],
            benzene: ['benzene', 'benzene_units'],
            particulate_matter: ['particulate_matter', 'pm_units'],
            sulfur_dioxide: ['sulfur_dioxide', 'so2_units'],
            carbon_monoxide: ['carbon_monoxide', 'co_units'],
            nitrogen_oxides: ['nitrogen_oxide', 'no_units'],
            tank_size_internal: ['internal_tank_size'],
            tank_size_external: ['external_tank_size'],
            // nitrous_oxide: ['nitrous_oxide', 'nitrous_oxide_units'], //nitrous oxide (NO2) is not a regulated substance
            carbon_dioxide: ['carbon_dioxide', 'carbon_dioxide_units'],
            methane: ['methane', 'methane_units']
          },
          onItemChange: (item: any, page?: Page) => {
            // start with the common fields and add or remove based on choices made in the form
            item._meta.display_fields = page.display_fields['common']

            // add the appropriate tank size fields based on tank location
            if (item.tank_location?.includes('external')) {
              item._meta.display_fields = item._meta.display_fields.toSpliced(
                item._meta.display_fields.indexOf('tank_location') + 1,
                0,
                page.display_fields['tank_size_external']
              )
            }
            if (item.tank_location?.includes('internal') || item.tank_location == undefined) {
              item._meta.display_fields = item._meta.display_fields.toSpliced(
                item._meta.display_fields.indexOf('tank_location') + 1,
                0,
                page.display_fields['tank_size_internal']
              )
            }

            // add/remove emission factors
            if (item.useAP42) {
              // AP42 default values will be used, don't add any emissions factor fields
              // remove document from display fields since it's not required if using AP42
              item._meta.display_fields = item._meta.display_fields.filter(item => item !== 'document')
            } else if (!item.useAP42) {
              // Not using AP42, add necessary emissions factors based on fuel/etc
              // add in all shared emission factors
              item._meta.display_fields = item._meta.display_fields.concat(
                page.display_fields['total_unburned_hydrocarbons'],
                page.display_fields['particulate_matter'],
                page.display_fields['sulfur_dioxide'],
                page.display_fields['carbon_monoxide'],
                page.display_fields['nitrogen_oxides'],
                page.display_fields['benzene'],
                page.display_fields['carbon_dioxide'],
                page.display_fields['methane']
              )

              // fuel type is undefined by default, but diesel is selected by default, include undefined to ensure correct fields are shown for diesel

              // if (['gasoline', 'diesel', undefined].includes(item.fuel_type)) {
              //   if (item.fuel_type == 'diesel' && item.brake_horsepower >= 600) {
              //     item._meta.display_fields = item._meta.display_fields.concat(
              //       page.display_fields['benzene']
              //     )
              //   }
              // } else if (['propane', 'butane'].includes(item.fuel_type)) {
              //   item._meta.display_fields = item._meta.display_fields.concat(
              //     page.display_fields['carbon_dioxide'],
              //     page.display_fields['methane'],
              //   )
              // }
            }
            if (!item.permit) {
              item.permit = this.attributes.globalid;
            }

            // if this isn't the first generator record it is not required and can be removed
            if (this.attributes.generators.indexOf(item) > 0) {
              item._meta.required = false;
            }
          }
        },
        this.contactsPage,
        this.facilityPage,
        {
          name: 'Insignificant Sources',
          item: 'insignificant_sources',
          route: 'application/standby/insignificant_sources',
          canActivate: false,
          service: new BaseService(`${this.environment.baseUrl}/applications/standby/insignificant_sources`, this.http),
          order: 4,
          required: false,
          display_fields: [
            'name', 'type', 'description'
          ],
          onItemChange: (item: any, page?: Page) => {
            item._meta.display_fields = page.display_fields;
            if (!item.permit) {
              item.permit = this.attributes.globalid;
            }
            item._meta.required = false;
          }
        },
        {
          name: 'Review',
          route: 'application/standby/review',
          item: 'review',
          service: this.standbyService,
          order: 5
        },
        {
          name: 'Complete',
          item: 'complete',
          service: this.standbyService,
          getPrintUrl: (id) => `${this.environment.baseUrl}/applications/standby/${id}/print_application/`,
          order: 6
        }
      );
      this.meta.fields.site.fields.shape.label_field = 'name';
      this.meta.fields.generators.child.fields.shape.label_field = 'unit_identification';
      this.title.next('Standby Generator Permit Application');
    } else if (type === 'standbyannualreport') {
      this.title.next('Standby Generator Annual Reporting');
      _pages.push({
          name: 'Generators',
          order: 1,
          item: 'generators',
          service: new BaseService(`${this.environment.baseUrl}/applications/standby/generatorannualreports`, this.http),
          display_fields: ['date_of_manufacture', 'unit_identification', 'description',
            'generator_make', 'generator_model', 'generator_serial_number',
            'engine_make', 'engine_model', 'engine_serial_number', 'previous_engine_hours', 'engine_hours', 'engine_hours_picture'],
          onItemChange: (item: any, page: Page) => {
            item._meta.display_fields = page.display_fields;
          }
        },
        {
          name: 'Review',
          item: 'review',
          service: this.standbyAnnualService,
          order: 2
        },
        {
          name: 'Complete',
          item: 'complete',
          service: new BaseService(`${this.environment.baseUrl}/applications/standby/annualreports`, this.http),
          order: 3,
          getPrintUrl: (id) => `${this.environment.baseUrl}/applications/standby/annualreports/${id}/print_application/`,

        });
    } else if (type === 'pesticides_noa') {
      this.title.next('Pesticide Notification of Arrival');
      _pages.push({
          ...this.contactsPage,
          route: 'application/pesticides_noa/contacts',
          order: 1,
          service: new BaseService(`${this.environment.baseUrl}/applications/pesticides/contacts`, this.http),
          onItemChange: (item: any, page?: Page) => {
            this.contactsPage.onItemChange(item, page);
            if (!item.noa) {
              item.noa = this.attributes.globalid;
            }
          }

        },
        {
          ...this.facilityPage,
          route: 'application/pesticides_noa/site',
          order: 2,
        },
        {
          name: 'Notice of Arrival',
          item: 'noa',
          route: 'application/pesticides_noa/self',
          order: 3,
          service: this.pesticidesService,
          // Keep NOA as page item while still binding to root API payload/meta.
          getAttributes: (attributes: any) => attributes,
          getMeta: (_attributes: any, meta: any) => meta,
          setAttributes: (attributes: any, value: any) => {
            Object.assign(attributes, value);
          },
          display_fields: ['products_file', 'origin', 'port_of_entry',
            'carrier', 'entry_number', 'entry_date', 'entry_date_estimated', 'marks', 'location', 'invoice_number', 'invoice_date', 'invoice_place',
            'confidential', 'importer_remarks', 'action', 'action_other',
            'vessel_id', 'vessel_eta'],
          // todo: fix?
          // post_validation: function () {
          //   // return Restangular.all('pesticides/noa_products').getList().then(function (response) {
          //   //   serviceObj.attributes.noa_products = response;
          //   // });
          // }
        },
        {
          name: 'Products',
          item: 'noa_products',
          service: new BaseService(`${this.environment.baseUrl}/applications/pesticides/noa/products`, this.http),
          route: 'application/pesticides_noa/noa_products',
          order: 4,
          display_fields: ['epa_registration_number', 'product_name', 'epa_status', 'container', 'sku', 'epa_establishment_number',
            'ingredients', 'unit_size', 'quantity', 'cases', 'remarks'],
          onItemChange: (item: any, page?: Page) => {
            item.noa = this.attributes.globalid;
            item._meta.display_fields = page.display_fields;
          }
        },
        {
          name: 'Review',
          item: 'review',
          service: this.pesticidesService,
          order: 5
        },
        {
          name: 'Complete',
          item: 'complete',
          order: 6,
          service: this.pesticidesService,
          getPrintUrl: (id) => `${this.environment.baseUrl}/applications/pesticides/noa/${id}/print_application/`,
        })
    } else if (type === 'burial_at_sea') {
      const isIssued = !!this.attributes?.issue_date;
      this.title.next(isIssued ? 'Burial at Sea – Final Burial Details' : 'Burial at Sea Application');
      _pages.push(
        {
          name: 'Burial Details',
          item: 'self',
          route: 'application/burial_at_sea/self',
          order: 2,
          service: this.burialAtSeaService,
          display_fields: isIssued
            ? ['deceased_name', 'remains_type', 'expected_burial_date', 'relation_to_deceased', 'at_request_of', 'death_certificate', 'shape', 'actual_burial_date', 'vessel_name', 'vessel_registration']
            : ['deceased_name', 'remains_type', 'expected_burial_date', 'relation_to_deceased', 'at_request_of', 'death_certificate'],
          onItemChange: (item: any, page?: Page) => {
            item._meta.display_fields = page.display_fields;
          }
        },
        {
          ...this.contactsPage,
          name: 'Applicant Info',
          route: 'application/burial_at_sea/contacts',
          service: new BaseService(`${this.environment.baseUrl}/applications/burialatsea/contacts`, this.http),
          order: 1,
          onItemChange: (item: any, page?: Page) => {
            this.contactsPage.onItemChange(item, page);
            if (!item.record) {
              item.record = this.attributes.globalid;
            }
          }
        },
        {
          name: 'Review',
          item: 'review',
          service: this.burialAtSeaService,
          order: 3,
          ...(isIssued ? { submitAction: 'submit_final' } : {})
        },
        {
          name: 'Complete',
          item: 'complete',
          service: this.burialAtSeaService,
          // No PDF is displayed on completion — the application PDF is emailed by staff on
          // acceptance, and the certificate is emailed by staff on finalization.
          order: 4
        }
      );
    }

    this.pages.next(_pages.sort((a: Page, b: Page) => a.order - b.order));
  }

  submit(currentPage) {
    return new Promise<void>((resolve, reject) => {
      var attributes: any = {};
      // var currentAttributes = this.attributes.restangularized === undefined ? this.attributes : this.attributes.plain();
      Object.keys(this.attributes).forEach(key => {
        if (Array.isArray(this.attributes[key])) {
          attributes[key] = this.attributes[key].map(function (obj) {
            return obj.globalid
          });
        } else if (this.attributes[key].hasOwnProperty('globalid')) {
          attributes[key] = this.attributes[key].globalid;
        } else {
          attributes[key] = this.attributes[key];
        }
      });

      if (this.attributes.globalid !== undefined) {
        attributes.globalid = this.attributes.globalid;
      }

      currentPage.service.save(attributes).then((item) => {
        this.attributes = item;
        resolve();
      }).catch(function (error) {
        reject();
        // errorHandling.applicationError(error, this.attributes);
      });
    })
  }

  validate(currentPage, currentAttributes): Observable<any> {
    // return new Promise<void>((resolve, reject) => {
    let post_validation = currentPage.hasOwnProperty('post_validation') ? currentPage.post_validation : () => of(true);

    if (Array.isArray(currentAttributes)) {
      let promises = [], scrollToHash;

      currentAttributes.forEach((item, index) => {
        // clear errors
        this.errorHandling.clearError(currentAttributes[index]._meta)
        let _deferred = currentPage.service.save(item).pipe(
          map((data: any) => {
            currentAttributes[index] = {...item, ...data};
            return item;
          }),
          catchError((error) => {
            this.errorHandling.fieldError(error, currentAttributes[index]._meta);
            return of(); // we want to capture all errors so we can't reraise the error
          })
        );
        // })
        promises.push(_deferred);

      });
      return combineLatest([...promises, post_validation()]);
    } else if (currentAttributes.verified) {
      // do not attempt to submit verified attributes
      return of(true);
    } else {
      const meta = this.getPageMeta(currentPage);
      this.errorHandling.clearError(meta);
      return currentPage.service.save(currentAttributes).pipe(
        tap((data: any) => {
          this.setPageAttributes(currentPage, {...currentAttributes, ...data});
        }),
        switchMap(() => post_validation()),
        catchError((error) => {
          const meta = this.getPageMeta(currentPage);
          this.errorHandling.fieldError(error, meta);
          throw error;
        })
      );
    }
    // })


  }

  showLoadingModal() {
    const loadingModalInstance = this.modalService.open(LoadingComponent, {backdrop: 'static'});
    loadingModalInstance.componentInstance.message = 'Loading Application';
    return loadingModalInstance;
  }


  async startApplication(program: string, force_resume: boolean = false) {
    const a = this.applicationTypes[program];

    let resume = false;
    if (document.cookie.indexOf('permittingtoken=') > -1) {
      await this.modalService.open(ResumeComponent).result.then(r => {
        if (r) {
          resume = true;
          this.loadApplication(program, true).subscribe();
        }
      })
      if (resume) {
        return
      }
    }

    if (program === 'burial_at_sea' && !force_resume) {
      this.loadApplication(program, false, {type: 'new'}).subscribe();
      return;
    }

    if (force_resume) {
      a.component = LoginComponent;
    }
    const additionalFieldMeta = await iif(() => 'extraFields' in this.applicationTypes[program],
      a.service.get('metadata'),
      of(true)
    ).toPromise()
    const modalInstance = this.modalService.open(a.component, {backdrop: 'static'});
    modalInstance.componentInstance.type = program;
    modalInstance.componentInstance.resume = force_resume;
    modalInstance.componentInstance.additionalFields = a.extraFields;
    modalInstance.componentInstance.additionalFieldsMeta = additionalFieldMeta;
    modalInstance.componentInstance.idFieldLabel = a.id_field_label;
  }

  verifyKey(program: string, data) {
    let service: BaseService;
    if (program === 'standby') {
      service = this.standbyService;
    }
    if (program === 'standbyannualreport') {
      service = this.standbyAnnualService;
    }
    if (program === 'pesticides_noa') {
      service = this.pesticidesService;
    }
    if (program === 'burial_at_sea') {
      service = this.burialAtSeaService;
    }

    return service.customPOST('verify_key', data);
  }

  loadApplication(program: string, resume = false, initValues = null) {
    let loadingModal: NgbModalRef = this.showLoadingModal();
    this.loadHelp(program);
    const promises = []
    if (program === 'standby') {
      promises.push(
        iif(
          () => resume,
          this.standbyService.get('self'),
          this.standbyService.post<any>({...initValues})
        ).pipe(
          tap(response => {
            this.meta = response.meta;
            this.meta.fields.site.fields.shape.label_field = 'name';
            this.meta.fields.generators.child.fields.shape.label_field = 'unit_identification';
            this.attributes = response.result
          })
        )
      );
    } else if (program === 'standbyannualreport') {
      promises.push(
        iif(
          () => resume,
          this.standbyAnnualService.get('self'),
          this.standbyAnnualService.post<any>({...initValues})
        ).pipe(tap(response => {
            this.attributes = response.result
            this.meta = response.meta;
          })
        )
      );
    } else if (program === 'pesticides_noa') {
      promises.push(iif(
        () => resume,
        this.pesticidesService.get('self'),
        this.pesticidesService.customPOST<any>('start', {...initValues})
      ).pipe(
        tap(response => {
          this.meta = response.meta;
          this.attributes = response.result;
          if (this.attributes.site.verified) {
            Object.keys(this.meta.fields.site.fields).forEach(k => this.meta.fields.site.fields[k].read_only = true);
          }
        })
      ))
    } else if (program === 'burial_at_sea') {
      promises.push(iif(
        () => resume,
        this.burialAtSeaService.get('self'),
        this.burialAtSeaService.customPOST<any>('start', {...initValues})
      ).pipe(
        tap(response => {
          this.meta = response.meta;
          this.attributes = response.result;
        })
      ));
    }
    // var loadingDeferred = new Promise((resolve, reject) => {
    //   this.http.get<any>(`${environment.base_url}/applications/pesticides/noaapplications/start`).toPromise()
    //     .then(function (meta) {
    //       this.meta = meta;
    //       this.attributes = {
    //         facility: {coordinates: {}, shape: {}},
    //         noa: {},
    //         noa_products: [{}],
    //         contacts: meta.fields.contacts.child.fields.relationship_type.choices
    //           .filter(function (type) {
    //             return type.value !== 'other';
    //           }).map(function (type) {
    //             return {relationship_type: type.value};
    //           })
    //       };
    //       this.isNew = true;
    //       var deferred = $q.defer();
    //       this.meta.fields.noa_products.child.fields.epa_registration_number.choices = [];
    //       this.meta.fields.noa_products.child.fields.epa_registration_number.get_choices = function (search) {
    //         if (!angular.equals({}, search)) {
    //           // used to cancel previous request
    //           deferred.resolve([]);
    //           // and start a new one
    //           deferred = $q.defer();
    //
    //           $http.get('https://ofmpub.epa.gov/apex/pesticides/ppls/' + search, {timeout: deferred.promise})
    //             .then(function (response) {
    //               var choices = response.data.items.map(function (item) {
    //                 return {
    //                   display_name: item.productname + ' (' + item.eparegno + ')',
    //                   value: item.eparegno,
    //                   orig_item: item
    //                 };
    //               });
    //               deferred.resolve(choices);
    //             });
    //         } else {
    //           deferred.resolve([]);
    //         }
    //         return deferred.promise;
    //       };
    //       this.meta.fields.noa_products.child.fields.epa_registration_number.select_choice = function (selected, item) {
    //         item.epa_registration_number = selected.orig_item.eparegno;
    //         item.product_name = selected.orig_item.productname;
    //         item.epa_status = selected.orig_item.product_status;
    //         item.ingredients = selected.orig_item.active_ingredients.map(function (ingredient) {
    //           return ingredient.active_ing + ' (' + ingredient.cas_number + ')';
    //         }).join(', ');
    //         var t = '';
    //         var p = '';
    //         for (var pdf in selected.orig_item.pdffiles) {
    //           var test_date = new Date(selected.orig_item.pdffiles[pdf].pdffile_accepted_date);
    //           if (t === '' || t < test_date) {
    //             t = test_date;
    //             p = selected.orig_item.pdffiles[pdf].pdffile;
    //           }
    //         }
    //         item.pdf_file = 'https://www3.epa.gov/pesticides/chem_search/ppls/' + p;
    //       };
    //
    //       loadingDeferred.resolve();
    //       // var modalInstance = $uibModal.open({
    //       //   templateUrl: '/static/modals/standbyAnnualYearChoices.html',
    //       //   controller: function ($uibModalInstance, $scope, $filter) {
    //       //     $scope.annual_reports = response;
    //       //     $scope.confirm = function () {
    //       //       this.attributes = $filter('filter')(response, {reporting_year: $scope.selected_year})[0];
    //       //       loadingDeferred.resolve();
    //       //       modalInstance.close();
    //       //     };
    //       //   },
    //       //   backdrop: 'static'
    //       // });
    //
    //
    //     });
    // })
    // promises.push(loadingDeferred);
    // }
    // else if (type === 'onestop') {
    //   var loadingDeferred = $q.defer();
    //   promises.push(loadingDeferred.promise);
    //   Restangular.one('onestop/applications', 'start').get().then(function (meta) {
    //     this.meta = meta;
    //     this.attributes = {
    //       facility: {coordinates: {}, shape: {}},
    //       permit: {},
    //       contacts: meta.fields.contacts.child.fields.relationship_type.choices
    //         .map(function (type) {
    //           return {relationship_type: type.value};
    //         })
    //     };
    //     this.isNew = true;
    //
    //     loadingDeferred.resolve();
    //
    //   });
    // }
    return iif(
      () => resume && !(document.cookie.indexOf('permittingtoken=') > -1),
      this.verifyKey(program, initValues),
      of(false)
    ).pipe(
      switchMap(() => forkJoin(promises)),
      tap(() => this.loadPages(program)),
      switchMap(() => this.pages),
      tap(pages => {
        let _activePage = pages[0].item;
        this.activePage.next(_activePage);
        loadingModal.close()
        this.router.navigate(['application', program, _activePage]);
      }),
      catchError(err => {
        loadingModal.close();
        throw err;
      })
    )
  }

  helpContent: Observable<{ field: string, help_text: string, help_label: string }[]>

  loadHelp(application_type: string) {
    this.helpContent = this.http.get(
      `${this.environment.baseUrl}/applications/help/`,
      {params: {application_type_in: [application_type, 'all'].join(','), page_size: 1000}}
    ).pipe(
      map((response: any) => response.results),
      shareReplay()
    )
  }

  getHelp(field): Observable<{ field: string, help_text: string, help_label: string }> {
    return this.helpContent.pipe(
      map(help => help.find(h => h.field === field)),
      filter(help => !!help),
    );
  }

  getPageAttributes(page: Page) {
    if (!page) {
      return undefined;
    }
    if (page.getAttributes) {
      return page.getAttributes(this.attributes, this.meta, page);
    }
    if (page.item === 'self') {
      return this.attributes;
    }
    return this.attributes[page.item];
  }

  getPageMeta(page: Page) {
    if (!page) {
      return undefined;
    }
    if (page.getMeta) {
      return page.getMeta(this.attributes, this.meta, page);
    }
    if (page.item === 'self') {
      return this.meta;
    }
    return this.meta?.fields?.[page.item];
  }

  setPageAttributes(page: Page, value: any) {
    if (!page) {
      return;
    }
    if (page.setAttributes) {
      page.setAttributes(this.attributes, value, page);
      return;
    }
    if (page.item === 'self') {
      this.attributes = {...this.attributes, ...value};
      return;
    }
    this.attributes[page.item] = value;
  }
}
